import { sql } from 'drizzle-orm'
import {
  type AnyPgColumn,
  bigint,
  boolean,
  check,
  index,
  integer,
  pgEnum,
  pgTable,
  smallint,
  text,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core'

import { createdAt, primaryId, taxRateBps, updatedAt } from './columns.ts'

/**
 * **One catalogue for everything FLOU sells** — a montage package, a camera for
 * the weekend, an hour in a room, a camera operator for a shoot day.
 *
 * This replaces five parallel catalogues (`service`+`service_tier`,
 * `rental_item`+`rental_category`, `studio`+`studio_feature`,
 * `specialist`+`specialist_deliverable`) that had each grown their own spelling
 * of the same five ideas: a name, a price, a bullet list, a sort order and an
 * active flag. Five spellings meant five panel screens, five loaders and a
 * five-armed `switch` in the cart, the pricing engine and the checkout.
 *
 * ## Why a single table works here
 *
 * The differences between a camera and a montage package are NOT a type. They
 * are three independent facts, and every one of them is a column:
 *
 * | column          | question it answers                                  |
 * |-----------------|------------------------------------------------------|
 * | `pricingUnit`   | what is one unit — a piece, an hour, a day, a month? |
 * | `bookingMode`   | does the buyer pick a date, and of what shape?        |
 * | `stockQuantity` | can two people buy this for the same day?             |
 *
 * A camera is `DAY / DATE_RANGE / 2`. A room is `HOUR / TIME_WINDOW / 1`. A
 * montage package is `PIECE / NONE / NULL` — unlimited, because selling the
 * same editing slot twice costs nothing but time. An operator is
 * `SHOOT_DAY / DAY / 1` plus a city list.
 *
 * Consumers ask "does this need a date?", never "is this a rental?", so a new
 * kind of offer costs zero new branches. That is the whole point of the merge,
 * and the reason a `type`/`kind` enum was deliberately NOT added: an enum would
 * have moved the five-armed switch from the database into the code and changed
 * nothing.
 *
 * ## Money
 *
 * Unchanged and non-negotiable: NET grosze in `bigint`, and a price is NULL
 * **iff** `isQuoteOnly`, enforced by a database CHECK on every priced table
 * (`*_price_quote_xor`). There is no representable "0 zł" for a quote-only
 * line, which is what structurally killed the bug where the string "Wycena
 * indywidualna" parsed to 0 and silently discounted a basket.
 */

/** What one billable unit of an offering is. */
export const offeringPricingUnit = pgEnum('offering_pricing_unit', [
  /** A thing: a thumbnail, a reel, a logo. Quantity is a count. */
  'PIECE',
  /** An hour of a room or a person. */
  'HOUR',
  /** A 24h rental day. */
  'DAY',
  /** A booked shoot day — priced per day but not a 24h hire. */
  'SHOOT_DAY',
  /** A retainer month, e.g. running someone's social media. */
  'MONTH',
  /**
   * A minute of finished film (spec §8 · G5).
   *
   * The quantity is not typed but CHOSEN, from ten length bands: the buyer
   * pays for the band's floor up front and its ceiling is a hard limit. That
   * is why this is its own unit rather than `PIECE` with `unit_label:
   * 'minuta'` — the difference is not the noun, it is that a screen showing a
   * stepper here would be asking a question ("how many minutes exactly?")
   * nobody can answer before the film is cut. See `film-pricing.ts`.
   */
  'MINUTE',
])

/**
 * What the buyer has to pick before this can be ordered, and therefore what
 * shape of reservation it produces. `NONE` is the majority case and is why
 * montage and graphics need no calendar at all.
 */
export const offeringBookingMode = pgEnum('offering_booking_mode', [
  /** No date. Ordered, queued, delivered. */
  'NONE',
  /** Inclusive range of whole days — equipment hire. */
  'DATE_RANGE',
  /** A start time and a duration — a room. */
  'TIME_WINDOW',
  /** One or more whole days on a person's calendar — a shoot. */
  'DAY',
])

/**
 * Where the work can happen. `ONLINE` is not "every city we happen to list" —
 * it means location is irrelevant, so the map highlights the whole country
 * instead of pins, and no city has to be added when a new one opens. `NONE` is
 * for offerings where the question does not arise (a room is where it is).
 */
export const offeringCoverageMode = pgEnum('offering_coverage_mode', ['NONE', 'ONLINE', 'CITIES'])

/**
 * Whether this offering needs the buyer to say what they want made, and how
 * hard that requirement is (spec §6, §7, §8).
 *
 * A capability column rather than a per-category branch, for the same reason
 * `bookingMode` is one: "does a thumbnail need a brief?" and "does a camera
 * hire need a brief?" are the same question asked of different rows, and the
 * answer belongs beside the row.
 *
 * Three states and not a boolean, because the screen needs all three. `NONE`
 * hides the field — a rental has nothing to brief. `OPTIONAL` shows it and
 * accepts an empty one. `REQUIRED` is spec §6's "brief tekstowy (wymagany)":
 * the checkout refuses the line without it, on the server, where a hidden
 * field cannot be re-enabled from a console.
 */
export const offeringBriefMode = pgEnum('offering_brief_mode', ['NONE', 'OPTIONAL', 'REQUIRED'])

/**
 * What a revision request has to POINT AT to count as concrete (spec §6, §8).
 *
 * Spec §6 refuses "nie podoba mi się" and asks for the element — "kolor
 * koszulki, tło, czapka". Spec §8 asks the same of montage in a different
 * currency: "klient wskazuje zakres znacznikami, np. «od 2:14 do 2:20»". Those
 * are not two policies, they are one policy expressed in the coordinates the
 * medium has. A thumbnail is a picture and you point at a THING in it; a reel
 * runs in time and you point at a MOMENT.
 *
 * A capability column rather than a branch on the category, for the reason at
 * the top of this file — and here it earns it twice over, because the next
 * time-shaped product (a podcast cut, a slideshow) needs the timecode rule
 * without anybody remembering that `montaz` was special.
 */
export const revisionAnchor = pgEnum('revision_anchor', ['ELEMENT', 'TIMECODE'])

/**
 * Whether this offering needs the buyer's own RAW MATERIAL before anybody can
 * start (spec §7, §8).
 *
 * Montage has nothing to edit without it: "klient wkleja link (Google Drive,
 * WeTransfer). Bez uploadu plików. Link wymagany od razu przy płatności." A
 * thumbnail needs none, a studio hire needs none, and a graphic may take
 * references without depending on them — which is why this has the same three
 * states as {@link offeringBriefMode} rather than being a boolean. The two
 * fields are asked for in the same place, on the same screen, under the same
 * rule: hidden at `NONE`, offered at `OPTIONAL`, refused-without at `REQUIRED`.
 */
export const offeringSourceMode = pgEnum('offering_source_mode', ['NONE', 'OPTIONAL', 'REQUIRED'])

/**
 * A page of the offer, and — through `parentId` — the groups inside it.
 *
 * Top-level rows ARE the public pages: Wynajem, Produkcja, Studio, Montaż,
 * Grafika. They carry the route and the page's own copy, so the heading a
 * visitor reads and the grouping the panel filters by are one row and cannot
 * drift. Children are groups within a page (Wynajem → Aparaty, Obiektywy) and
 * leave `href` NULL: they are a heading on their parent's page, not a route.
 */
export const offeringCategory = pgTable(
  'offering_category',
  {
    id: primaryId('ocat'),
    /** NULL for a page; set for a group inside one. One level, not a tree. */
    parentId: text('parent_id').references((): AnyPgColumn => offeringCategory.id, {
      onDelete: 'restrict',
    }),
    slug: text('slug').notNull(),
    name: text('name').notNull(),
    /** The public route, e.g. `/pl/wynajem`. NULL on child groups. */
    href: text('href'),
    /** Glyph name from the closed set the client maps to a component. */
    icon: text('icon'),

    /** The page's own copy — heading and lead paragraph. */
    title: text('title'),
    lead: text('lead'),

    sortOrder: integer('sort_order').notNull().default(0),
    isActive: boolean('is_active').notNull().default(true),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    uniqueIndex('offering_category_slug_unique').on(t.slug),
    index('offering_category_parent_idx').on(t.parentId, t.sortOrder),
    // A group belongs to a page; a page belongs to nobody. Enforcing one level
    // here means a reader never has to ask how deep the tree can go, and the
    // panel's category picker is a flat two-level list by construction.
    check('offering_category_href_or_parent', sql`${t.parentId} IS NULL OR ${t.href} IS NULL`),
  ],
)

/**
 * One thing on offer. Everything the buyer sees on a card lives here; what it
 * costs lives on its variants, because even the simplest offering has at least
 * one and most have three.
 */
export const offering = pgTable(
  'offering',
  {
    id: primaryId('off'),
    categoryId: text('category_id')
      .notNull()
      .references(() => offeringCategory.id, { onDelete: 'restrict' }),

    /** Stable key used by the cart, routes and analytics, e.g. `miniaturka`. */
    slug: text('slug').notNull(),
    name: text('name').notNull(),
    /** Short label above the name: `Studio 01`, `Strateg kanału`. */
    kicker: text('kicker'),
    /** One-line pitch under the name. */
    blurb: text('blurb'),
    /** The long version, for a detail page. */
    description: text('description'),
    /** Glyph name; cards that have no photograph draw this instead. */
    icon: text('icon'),

    // ── the three capability columns ──────────────────────────────────────
    pricingUnit: offeringPricingUnit('pricing_unit').notNull().default('PIECE'),
    bookingMode: offeringBookingMode('booking_mode').notNull().default('NONE'),
    /**
     * How many identical units exist. **NULL means unlimited** — not zero, and
     * not one. Editing work has no stock; a camera has two bodies; a room has
     * itself. Availability checks skip the offering entirely when this is NULL,
     * which is what keeps a montage order off the calendar.
     */
    stockQuantity: integer('stock_quantity'),

    coverageMode: offeringCoverageMode('coverage_mode').notNull().default('NONE'),

    /**
     * What the buyer has to write before this can be ordered. See
     * {@link offeringBriefMode}. Defaults to `NONE` so a catalogue row added
     * without thinking about it asks for nothing rather than blocking checkout.
     */
    briefMode: offeringBriefMode('brief_mode').notNull().default('NONE'),

    /**
     * What a revision request on this offering has to point at. See
     * {@link revisionAnchor}. `ELEMENT` is the default because it is the weaker
     * requirement: a catalogue row added without thinking about it asks for a
     * description rather than for a timecode nobody can supply about a still
     * image.
     *
     * How LONG the performer then has is deliberately not a second column —
     * `revisionHoursFor` in `@repo/core` halves `turnaroundHours`, which
     * reproduces both of the spec's numbers (24 h thumbnail → 12 h revision,
     * day-shaped montage → 24 h) from a promise that is already stored.
     */
    revisionAnchor: revisionAnchor('revision_anchor').notNull().default('ELEMENT'),

    /**
     * Whether the buyer has to hand over source material. See
     * {@link offeringSourceMode}. `NONE` by default, so a catalogue row added
     * without thinking about it asks for nothing rather than blocking checkout.
     */
    sourceMode: offeringSourceMode('source_mode').notNull().default('NONE'),

    // ── quantity vocabulary, for the creator and the cart ─────────────────
    /** What a single unit is called, e.g. `szt.`, `rolka`, `godzina`. */
    unitLabel: text('unit_label'),
    /** Polish needs three plural forms — 1 / 2–4 / 5+. */
    unitFormOne: text('unit_form_one'),
    unitFormFew: text('unit_form_few'),
    unitFormMany: text('unit_form_many'),
    /** Label above the quantity control, e.g. `Ile rolek?`. */
    quantityLabel: text('quantity_label'),
    defaultQuantity: integer('default_quantity').notNull().default(1),

    // ── booking detail, used only by the modes that need it ───────────────
    /** Shortest bookable block for `TIME_WINDOW`. */
    minBookingMinutes: integer('min_booking_minutes'),
    /**
     * Unpaid minutes this offering needs on EACH side of a reservation, or
     * NULL to take whatever its booking mode implies.
     *
     * `bufferMinutesFor` in `@repo/core` keys the buffer on the MODE, and its
     * docblock argues the case: a room needs an hour to be reset, a hire needs
     * the days it spends in transit. That reasoning is sound and it is also
     * why this column exists — because it makes every `TIME_WINDOW` offering a
     * room, and one of them is not. A strategy meeting is an hour of somebody's
     * attention: it needs no teardown, and buffering it like a studio turned a
     * twelve-hour office day into four bookable slots.
     *
     * NULL rather than a default of 60, so the column says "this offering has
     * nothing to say about buffers" instead of restating the mode's answer in
     * a place that could then drift from it. Zero is a real value and means
     * exactly zero.
     */
    bufferMinutes: smallint('buffer_minutes'),
    /** Refundable deposit, NET grosze. NULL = none taken; 0 is not a value. */
    depositNetMinor: bigint('deposit_net_minor', { mode: 'number' }),

    /**
     * How long, in hours, this offering promises to take from the order.
     *
     * **NULL means the promise is a DAY, not a duration** — the deadline is the
     * end of the first day with free capacity. Spec §7 says so of montage in as
     * many words ("Według dostępności freelancera, nie sztywne 24h"), while
     * spec §6 gives thumbnails a flat 24. Both models are real and the
     * difference is per offering, not per level: every level of Miniatura
     * promises 24 h, every level of Montaż promises a date.
     *
     * A capability COLUMN rather than a `kind` enum, for the reason stated at
     * the top of this file: an enum would need a new member (and a new branch
     * everywhere that reads it) the first time somebody sells a 48-hour rush
     * option, whereas this already answers that with a `48`.
     */
    turnaroundHours: integer('turnaround_hours'),
    /** Human phrasing, e.g. `2–4 osoby przed kamerą`. */
    capacityLabel: text('capacity_label'),
    /** Machine-readable ceiling for filtering; NULL when unspecified. */
    capacityMax: integer('capacity_max'),

    // ── pricing-engine eligibility ────────────────────────────────────────
    /**
     * Per-piece work, so the VOLUME ladder applies to a line of it. Rentals
     * discount by days instead and must never pick this up.
     */
    isVolumeEligible: boolean('is_volume_eligible').notNull().default(false),
    /** Counts toward the MULTI_SERVICE basket bonus. */
    countsTowardMultiService: boolean('counts_toward_multi_service').notNull().default(true),
    /** Listed in the creator, which only sizes exact-priced work. */
    isInCreator: boolean('is_in_creator').notNull().default(false),

    sortOrder: integer('sort_order').notNull().default(0),
    isActive: boolean('is_active').notNull().default(true),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    uniqueIndex('offering_slug_unique').on(t.slug),
    index('offering_category_idx').on(t.categoryId, t.sortOrder),
    index('offering_active_idx').on(t.isActive, t.sortOrder),
    // NULL is the only way to say "unlimited"; a stock of 0 would mean an
    // offering that is active and can never be booked, which is what
    // `isActive` is for.
    check('offering_stock_positive', sql`${t.stockQuantity} IS NULL OR ${t.stockQuantity} > 0`),
    // A deposit of 0 is not "no deposit" — NULL is. Keep the two distinct.
    check(
      'offering_deposit_positive',
      sql`${t.depositNetMinor} IS NULL OR ${t.depositNetMinor} > 0`,
    ),
    // Same rule, same reason: NULL means "the deadline is a day". A stored 0
    // would mean a piece of work due the instant it is paid for, which is not a
    // promise anybody can make — and it would read as "no turnaround" to a
    // careless `?? 0`.
    check(
      'offering_turnaround_positive',
      sql`${t.turnaroundHours} IS NULL OR ${t.turnaroundHours} > 0`,
    ),
    check(
      'offering_min_booking_positive',
      sql`${t.minBookingMinutes} IS NULL OR ${t.minBookingMinutes} > 0`,
    ),
    check(
      'offering_buffer_minutes_nonneg',
      sql`${t.bufferMinutes} IS NULL OR ${t.bufferMinutes} >= 0`,
    ),
  ],
)

/**
 * What you actually buy and what it costs: `Starter`, `Pro`, `doba`,
 * `pół dnia`. Every offering has at least one — a camera's single "doba"
 * variant is not ceremony, it is the row that lets the same cart line, the same
 * price snapshot and the same discount ladder serve equipment and editing
 * alike. It is also what finally lets two rooms carry different blocks, which
 * the old `studio` + shared-service split could not express.
 */
export const offeringVariant = pgTable(
  'offering_variant',
  {
    id: primaryId('ovar'),
    offeringId: text('offering_id')
      .notNull()
      .references(() => offering.id, { onDelete: 'cascade' }),
    /** Stable key within the offering, e.g. `creator`, `doba`. */
    slug: text('slug').notNull(),
    /** Display name and cart line label. */
    name: text('name').notNull(),
    description: text('description'),

    /**
     * NET price in grosze. NULL **iff** `isQuoteOnly` — the pair is the whole
     * point: no code path can read a missing price as zero.
     */
    priceNetMinor: bigint('price_net_minor', { mode: 'number' }),
    /**
     * What the PERFORMER is paid for one unit of this level, NET grosze
     * (spec §17 · J1).
     *
     * > Stawka freelancera jest stała kwotowo, nie procentowa. Przykład: rolka
     * > 100 zł na stronie, freelancer dostaje 50 zł (stałe).
     *
     * An AMOUNT and never a percentage, and that is the whole rule rather than
     * a storage detail: a percentage would move with every discount, and spec
     * §17 is explicit that it must not — "rabat obciąża marżę FLOU, nie
     * freelancera. Przy rabacie 20 procent klient płaci 80 zł, freelancer nadal
     * 50 zł, FLOU zarabia 30 zł." Stored as grosze, the margin falls out by
     * subtraction, and there is no rung anywhere that can touch it.
     *
     * NULL means "nobody is paid per unit for this", which is most of the
     * catalogue: a camera hire, a room by the hour and a quote-only line have
     * no performer to pay. It is NOT zero — zero would be a rate somebody set,
     * and the two read very differently on a payout report.
     *
     * Changed by an Admin and by nothing else. Spec §17: "Stawkę freelancera
     * zmienia tylko ręcznie Admin" — there is no rule in this system that
     * derives it, which is why it is a plain editable column.
     */
    performerRateNetMinor: bigint('performer_rate_net_minor', { mode: 'number' }),
    isQuoteOnly: boolean('is_quote_only').notNull().default(false),
    taxRateBps: taxRateBps(),
    /** Price suffix as shown, e.g. `/ szt.`, `/ h`, `/ mies.`. */
    priceSuffix: text('price_suffix'),

    /**
     * How long this variant occupies the calendar, in minutes. NULL for
     * anything with `bookingMode = NONE`. This is what tells the booking form
     * that "pół dnia" is four hours and "doba" is 1440 minutes, instead of the
     * name being parsed or the duration being hardcoded per page.
     */
    durationMinutes: integer('duration_minutes'),

    /**
     * WHERE this level happens — `ONLINE`, `OFFICE`, or NULL for everything
     * that is not a meeting.
     *
     * Owner, about the paid strategy hour: *„w zakładce kontakt jako 2 kafelki
     * do wyboru ale tak naprawdę to 1 pozycja różnica taka że jedno online
     * drugie stacjonarnie odzielimy to jakoś kolorami w kalendarzu"*. The free
     * call had asked the question since it was written; the paid one never did,
     * so the office got an hour in the calendar with no idea whether the
     * customer was driving to Radomierzyce or waiting for a link.
     *
     * On the VARIANT rather than on the offering, because that is what the two
     * tiles are: one position in the price list at one price, sold as two
     * levels. And a column rather than a list of slugs in the code — a
     * catalogue that is edited in the panel cannot have its meaning kept in a
     * `switch` somewhere else.
     *
     * `booking.meeting_place` is the same two words, copied onto the
     * reservation at checkout so the calendar can colour it.
     */
    meetingPlace: text('meeting_place'),

    // ── what the framework contract's Zalacznik 1 asks every Zlecenie to say ──
    // Defaults per LEVEL, snapshotted onto the line at checkout. They live on
    // the variant and not the offering because all three genuinely differ by
    // level: Advanced is sold more rounds than Clean, exports at a higher spec, and
    // is the tier allowed paid stock.

    /**
     * Rounds of revisions included in this level's price.
     *
     * NULL means "the house rule" — `FREE_REVISION_ROUNDS` in
     * `@repo/core/orders/revisions` — and is the right answer for most of the
     * catalogue: a camera hire and a studio hour have no revisions to include,
     * and a stored 2 there would be a promise nobody makes. It is NOT zero;
     * zero is a level sold with no rounds at all, which is a thing somebody
     * chose.
     *
     * Reading it is what finally settles the disagreement written up over
     * `FREE_REVISION_ROUNDS`: the price matrix sells 1/2/3 rounds by level
     * while the code gives everyone 2. Until an Admin fills this in, the
     * constant still decides — so adding the column changes nothing by itself.
     */
    revisionRoundsIncluded: smallint('revision_rounds_included'),

    /**
     * How long the PERFORMER has at this level, in clock hours (owner, 14.10:
     * rolka Clean 24 h, Dynamic 48 h, Advanced 72 h).
     *
     * Setting it moves the level onto the working clock in
     * `@repo/core/capacity/work-clock.ts`: the clock starts inside 8:00–18:00
     * and runs only across the days the assigned performer declared. NULL
     * leaves the level on the offering's own promise (`offering.turnaround_hours`),
     * which is how every level worked before this column existed.
     */
    turnaroundHours: smallint('turnaround_hours'),

    /**
     * What the CLIENT is promised at this level, in hours — the performer's
     * time plus FLOU's margin (rolka 48 / 72 / 96 h). Only read when
     * `turnaround_hours` is set; the client's date is the performer's deadline
     * moved by the difference, stamped as `order_item.client_due_at`.
     */
    clientTurnaroundHours: smallint('client_turnaround_hours'),

    /**
     * The technical parameters the finished file must meet — resolution, codec,
     * frame rate, aspect. Free text, because it is read by people and the set
     * of things worth saying differs per product.
     */
    exportSpec: text('export_spec'),

    /**
     * Third-party material the performer may use at this level: music, stock,
     * fonts. Free text for the same reason, and NULL means "the Zlecenie says",
     * not "anything goes".
     */
    thirdPartyMaterials: text('third_party_materials'),

    isPopular: boolean('is_popular').notNull().default(false),
    sortOrder: integer('sort_order').notNull().default(0),
    isActive: boolean('is_active').notNull().default(true),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    uniqueIndex('offering_variant_slug_unique').on(t.offeringId, t.slug),
    index('offering_variant_offering_idx').on(t.offeringId, t.sortOrder),
    // The pair every priced table in this schema carries — see the block
    // comment at the top of this file.
    check('offering_variant_price_quote_xor', sql`(${t.priceNetMinor} IS NULL) = ${t.isQuoteOnly}`),
    check(
      'offering_variant_price_positive',
      sql`${t.priceNetMinor} IS NULL OR ${t.priceNetMinor} > 0`,
    ),
    // The ceiling is the same one `order_item.revision_round` carries, minus
    // the round spent accepting: a level cannot include more rounds than the
    // counter can count, or the first sale of it would fail at the database.
    check(
      'offering_variant_revision_rounds_range',
      sql`${t.revisionRoundsIncluded} IS NULL
          OR (${t.revisionRoundsIncluded} >= 0 AND ${t.revisionRoundsIncluded} <= 10)`,
    ),
    // A rate of zero is not a rate — see the column's note. Negative would be
    // the performer paying to work.
    check(
      'offering_variant_performer_rate_positive',
      sql`${t.performerRateNetMinor} IS NULL OR ${t.performerRateNetMinor} > 0`,
    ),
    // `tax_rate_bps` is `smallint`, so a negative rate is representable and
    // would make VAT a rebate. Declared per table because a drizzle CHECK
    // cannot live on the shared column builder in `columns.ts`.
    check(
      'offering_variant_tax_rate_range',
      sql`${t.taxRateBps} >= 0 AND ${t.taxRateBps} <= 10000`,
    ),
    check(
      'offering_variant_duration_positive',
      sql`${t.durationMinutes} IS NULL OR ${t.durationMinutes} > 0`,
    ),
    // The same two words `booking_meeting_place_known` allows, because the one
    // is copied into the other at checkout: a third value here would reach a
    // reservation the calendar cannot colour.
    check(
      'offering_variant_meeting_place_known',
      sql`${t.meetingPlace} IS NULL OR ${t.meetingPlace} IN ('ONLINE', 'OFFICE')`,
    ),
  ],
)

/**
 * The rows of one offering's comparison table — its left-hand column. Ordered
 * once here, so every variant card lists features in the same sequence and only
 * the included/excluded split moves as you step up.
 *
 * This is also where the old `specialist_deliverable` and `studio_feature` flat
 * lists land, and they gain a comparison matrix by arriving: "raport miesięczny
 * jest w Standard, nie ma go w Start" was not expressible before.
 */
export const offeringFeature = pgTable(
  'offering_feature',
  {
    id: primaryId('ofeat'),
    offeringId: text('offering_id')
      .notNull()
      .references(() => offering.id, { onDelete: 'cascade' }),
    /** Stable key, e.g. `napisy`. Lets a label be reworded without re-linking. */
    key: text('key').notNull(),
    label: text('label').notNull(),
    sortOrder: integer('sort_order').notNull().default(0),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    uniqueIndex('offering_feature_key_unique').on(t.offeringId, t.key),
    index('offering_feature_offering_idx').on(t.offeringId, t.sortOrder),
  ],
)

/**
 * What a variant grants for a feature. A missing row means "not included" — the
 * card renders the row dimmed so the reader can see what stepping up buys.
 * `valueLabel` is the qualifier (`do 120 min`, `2 posty / tydz.`); NULL with
 * `isIncluded` true renders as a plain tick.
 */
export const offeringVariantFeature = pgTable(
  'offering_variant_feature',
  {
    id: primaryId('ovfeat'),
    offeringVariantId: text('offering_variant_id')
      .notNull()
      .references(() => offeringVariant.id, { onDelete: 'cascade' }),
    offeringFeatureId: text('offering_feature_id')
      .notNull()
      .references(() => offeringFeature.id, { onDelete: 'cascade' }),
    isIncluded: boolean('is_included').notNull().default(true),
    valueLabel: text('value_label'),

    createdAt: createdAt(),
  },
  (t) => [
    uniqueIndex('offering_variant_feature_unique').on(t.offeringVariantId, t.offeringFeatureId),
    index('offering_variant_feature_feature_idx').on(t.offeringFeatureId),
  ],
)

/**
 * Price-free options a client picks when asking for a quote — a wizytówka, a
 * logo, an ad set. They deliberately carry NO price column: scope varies too
 * much between a business card and a full campaign, and inventing a number here
 * is exactly the failure mode `isQuoteOnly` exists to prevent.
 */
export const offeringQuoteOption = pgTable(
  'offering_quote_option',
  {
    id: primaryId('oqopt'),
    offeringId: text('offering_id')
      .notNull()
      .references(() => offering.id, { onDelete: 'cascade' }),
    slug: text('slug').notNull(),
    label: text('label').notNull(),
    blurb: text('blurb'),
    /** The catch-all ("Inne / indywidualne") — renders last and asks for a note. */
    isCustom: boolean('is_custom').notNull().default(false),
    sortOrder: integer('sort_order').notNull().default(0),
    isActive: boolean('is_active').notNull().default(true),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    uniqueIndex('offering_quote_option_slug_unique').on(t.offeringId, t.slug),
    index('offering_quote_option_offering_idx').on(t.offeringId, t.sortOrder),
  ],
)

/**
 * A concrete window on an offering's calendar. `isBookable` false marks a
 * blackout (maintenance, a private shoot); true marks an explicitly offered
 * slot.
 *
 * One table for both because the calendar only ever asks one question — "is
 * this interval offered?" — and a second table would have to be joined on every
 * read to answer it. Absence of any row means "follow the default opening
 * hours", which live in `site_setting`.
 *
 * NOTHING READS THIS TABLE YET, and that is worth saying out loud rather than
 * leaving to be discovered. Availability today is decided entirely by
 * `booking` overlaps against `offering.stock_quantity` (see
 * `queries/availability.ts`), which answers "is anything else booked?" but not
 * "is this offered at all?" — so a blackout for maintenance, a closed week, or
 * a Sunday nobody works has no way to be expressed and no effect on what a
 * customer can pick.
 *
 * It stays because P2 owns every table and inventing it later under a different
 * name would split the calendar in two. Wiring it means reading it in the same
 * transaction as the overlap check.
 */
export const offeringAvailability = pgTable(
  'offering_availability',
  {
    id: primaryId('oavl'),
    offeringId: text('offering_id')
      .notNull()
      .references(() => offering.id, { onDelete: 'cascade' }),
    startsAt: timestamp('starts_at', { withTimezone: true }).notNull(),
    endsAt: timestamp('ends_at', { withTimezone: true }).notNull(),
    isBookable: boolean('is_bookable').notNull().default(true),
    note: text('note'),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('offering_availability_offering_idx').on(t.offeringId, t.startsAt),
    check('offering_availability_period_ordered', sql`${t.endsAt} > ${t.startsAt}`),
  ],
)

/**
 * ONE physical copy of something (spec §11 · I5).
 *
 * > Każdy egzemplarz ma własny kalendarz (egzemplarz 1, 2, 3).
 *
 * `offering.stock_quantity` said there were three cameras. It could not say
 * WHICH three, so a reservation held "one of the stock" and availability was a
 * count: three overlapping bookings fitted, four did not. That works right up
 * until somebody asks which body goes out on Friday, or one of them comes back
 * with a broken mount and has to leave the pool for a fortnight — neither
 * question has an answer in a number.
 *
 * A row per copy answers both, and it is also what makes the overlap
 * enforceable IN THE DATABASE: `booking_unit_no_overlap` is an EXCLUDE
 * constraint over `(unit_id, blocked period)`, which the `booking` table has
 * owed since it was written ("Overlap is NOT enforced at the database level
 * yet"). A count cannot carry that constraint; an identity can.
 *
 * `stock_quantity` stays for everything that genuinely is a count — a room
 * holds four people, not four rooms — and for equipment it becomes a derived
 * read: how many ACTIVE units there are.
 */
export const offeringUnit = pgTable(
  'offering_unit',
  {
    id: primaryId('ounit'),
    offeringId: text('offering_id')
      .notNull()
      .references(() => offering.id, { onDelete: 'cascade' }),
    /** What the office calls it: "Korpus 1", "A7 III · czarny". */
    label: text('label').notNull(),
    /** The number on the sticker, when there is one. Unique per offering. */
    serialNumber: text('serial_number'),
    /**
     * Out of the pool without being deleted — repairs, a loan, a write-off.
     *
     * Deactivated rather than removed, because bookings reference it: a camera
     * that went in for service still has to say which hires it went out on.
     */
    isActive: boolean('is_active').notNull().default(true),
    note: text('note'),
    sortOrder: integer('sort_order').notNull().default(0),

    createdAt: createdAt(),
    updatedAt: updatedAt(),
  },
  (t) => [
    index('offering_unit_offering_idx').on(t.offeringId, t.sortOrder),
    // Two bodies with the same serial is a typo, and the one it produces is a
    // camera that exists twice in the calendar.
    uniqueIndex('offering_unit_serial_unique')
      .on(t.offeringId, t.serialNumber)
      .where(sql`${t.serialNumber} IS NOT NULL`),
    check('offering_unit_label_present', sql`length(trim(${t.label})) > 0`),
  ],
)

export type OfferingCategory = typeof offeringCategory.$inferSelect
export type NewOfferingCategory = typeof offeringCategory.$inferInsert
/**
 * The booking-mode enum as a TYPE, so a function can branch on it without
 * re-listing the four members. Derived from the pgEnum rather than written out,
 * or a fifth mode would silently type-check everywhere it is not handled.
 */
/**
 * The two pages of the offer that ARE a physical resource with a calendar of
 * its own — the shelf and the room.
 *
 * Named here, beside the category table, because three unrelated readers ask
 * the same question and must not answer it from three hardcoded strings: the
 * panel's two calendars split on it (`bookingKindOf`), the chat scope opens the
 * conversations of an order that books one (`listThreads`), and both would
 * silently stop matching the day somebody renames a page.
 *
 * PAGE slugs, not category slugs: a camera sits under `aparat` under `wynajem`,
 * so every reader coalesces a group with its parent before comparing.
 */
export const RENTAL_PAGE_SLUG = 'wynajem'
export const STUDIO_PAGE_SLUG = 'studio'
export const PHYSICAL_RESOURCE_PAGES: readonly string[] = [RENTAL_PAGE_SLUG, STUDIO_PAGE_SLUG]

export type OfferingBookingMode = (typeof offeringBookingMode.enumValues)[number]

/** Same derivation for the revision anchor — see {@link revisionAnchor}. */
export type RevisionAnchor = (typeof revisionAnchor.enumValues)[number]

/** …and for the source-material requirement — see {@link offeringSourceMode}. */
export type OfferingSourceMode = (typeof offeringSourceMode.enumValues)[number]

export type Offering = typeof offering.$inferSelect
export type NewOffering = typeof offering.$inferInsert
export type OfferingVariant = typeof offeringVariant.$inferSelect
export type NewOfferingVariant = typeof offeringVariant.$inferInsert
export type OfferingFeature = typeof offeringFeature.$inferSelect
export type OfferingQuoteOption = typeof offeringQuoteOption.$inferSelect
export type OfferingAvailability = typeof offeringAvailability.$inferSelect
export type OfferingUnit = typeof offeringUnit.$inferSelect
export type NewOfferingUnit = typeof offeringUnit.$inferInsert
